import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { ContainerSize } from '@freelancer/ui/container';
import { HorizontalAlignment, VerticalAlignment } from '@freelancer/ui/grid';
import {
  HeadingColor,
  HeadingType,
  HeadingWeight,
} from '@freelancer/ui/heading';
import { PictureDisplay, PictureObjectFit } from '@freelancer/ui/picture';
import { FontColor, FontType, TextSize } from '@freelancer/ui/text';
import {
  SectionFeatureAlignment,
  SectionFeatureBgColor,
  SectionFeatureColor,
  SectionFeatureSize,
} from './section-feature.types';

@Component({
  selector: 'fl-section-feature-body',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionFeatureBodyComponent {}

@Component({
  selector: 'fl-section-feature-detail',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionFeatureDetailComponent {}

@Component({
  selector: 'fl-section-feature-heading',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionFeatureHeadingComponent {}

@Component({
  selector: 'fl-section-feature-subheading',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionFeatureSubheadingComponent {}

@Component({
  selector: 'fl-section-feature-button',
  template: `
    <fl-button
      class="SectionAButton"
      [color]="color"
      [size]="ButtonSize.LARGE"
      [sizeDesktop]="ButtonSize.XLARGE"
      [link]="link"
      (click)="buttonClicked.emit()"
      [busy]="busy"
    >
      <ng-content></ng-content>
    </fl-button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionFeatureButtonComponent {
  ButtonSize = ButtonSize;

  @Input() color: ButtonColor = ButtonColor.DEFAULT;
  @Input() link: string;
  @Input() busy: boolean;
  @Output() buttonClicked = new EventEmitter<void>();
}

@Component({
  selector: 'fl-section-feature-button-detail',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionFeatureButtonDetailComponent {}

@Component({
  selector: 'fl-section-feature',
  template: `
    <div
      class="SectionFeature"
      [attr.data-size]="size"
      [attr.data-border-top]="borderTop"
      [attr.data-image-overflow]="imageOverflow"
      [attr.data-overlay]="overlay"
      [attr.data-full-width]="fullWidth"
      [attr.data-color]="color"
      [attr.data-bg-color]="backgroundColor"
    >
      <fl-container
        [size]="imageOverflow ? ContainerSize.FLUID : containerSize"
      >
        <fl-grid
          [vAlign]="VerticalAlignment.VERTICAL_CENTER"
          [hAlign]="HorizontalAlignment.HORIZONTAL_CENTER"
        >
          <fl-col
            class="SectionFeature-content"
            [attr.data-image-overflow]="imageOverflow"
            [col]="12"
            [colDesktopSmall]="
              hideImageDesktopSmall
                ? 12
                : fullWidth
                  ? 8
                  : !isImageMainContent
                    ? 7
                    : 5
            "
            [colDesktopLarge]="fullWidth ? 8 : !isImageMainContent ? 7 : 5"
            [colOrder]="contentColOrder"
            [colTabletOrder]="contentColOrderTablet"
            [colDesktopSmallOrder]="contentColOrderDesktopSmall"
            [pull]="fullWidth ? undefined : 'right'"
            [flMarginBottom]="
              alignment === SectionFeatureAlignment.LEFT ? 'large' : 'none'
            "
            [flMarginBottomDesktop]="
              fullWidth && alignment === SectionFeatureAlignment.LEFT
                ? 'large'
                : 'none'
            "
            [flAnimateOnScroll]="animateOnScroll"
            [flAnimateOnScrollBehavior]="
              alignment === SectionFeatureAlignment.RIGHT && animateOnScroll
                ? AnimationBehavior.FADE_FROM_RIGHT
                : AnimationBehavior.FADE_FROM_LEFT
            "
          >
            <fl-text
              *ngIf="sectionFeatureDetail"
              [color]="
                color === SectionFeatureColor.DARK
                  ? FontColor.FOREGROUND
                  : FontColor.LIGHT
              "
              [fontType]="FontType.PARAGRAPH"
              [size]="TextSize.XSMALL"
              [flMarginBottom]="'small'"
            >
              <ng-content select="fl-section-feature-detail"></ng-content>
            </fl-text>
            <fl-heading
              *ngIf="sectionFeatureHeading"
              [color]="
                color === SectionFeatureColor.DARK
                  ? HeadingColor.FOREGROUND
                  : HeadingColor.LIGHT
              "
              [headingType]="HeadingType.H2"
              [size]="TextSize.LARGE"
              [sizeTablet]="TextSize.XLARGE"
              [sizeDesktop]="TextSize.XXLARGE"
              [flMarginBottom]="'mid'"
            >
              <ng-content select="fl-section-feature-heading"></ng-content>
            </fl-heading>
            <fl-text
              *ngIf="sectionFeatureSubheading"
              [color]="
                color === SectionFeatureColor.DARK
                  ? FontColor.FOREGROUND
                  : FontColor.LIGHT
              "
              [size]="TextSize.MARKETING_SMALL"
              [sizeDesktop]="TextSize.MID"
              [flMarginBottom]="'small'"
              [flAnimateOnScroll]="animateOnScroll"
            >
              <ng-content select="fl-section-feature-subheading"></ng-content>
            </fl-text>
            <div>
              <fl-text
                *ngIf="sectionFeatureBody"
                [color]="
                  color === SectionFeatureColor.DARK
                    ? FontColor.FOREGROUND
                    : FontColor.LIGHT
                "
                [fontType]="FontType.PARAGRAPH"
                [size]="TextSize.SMALL"
                [sizeDesktop]="TextSize.MARKETING_SMALL"
                [flMarginBottom]="sectionFeatureButton ? 'xlarge' : 'none'"
              >
                <ng-content select="fl-section-feature-body"></ng-content>
              </fl-text>
              <div
                *ngIf="sectionFeatureButton"
                [flMarginBottom]="
                  sectionFeatureButtonDetail ? 'xxsmall' : 'none'
                "
              >
                <ng-content select="fl-section-feature-button"></ng-content>
              </div>
              <p class="text-xxsmall">
                <ng-content
                  select="fl-section-feature-button-detail"
                ></ng-content>
              </p>
            </div>
          </fl-col>
          <fl-col
            class="SectionFeature-image"
            [col]="12"
            [flHideMobile]="hideImageMobile"
            [flHideTablet]="hideImageTablet"
            [attr.data-image-overflow]="imageOverflow"
            [attr.data-hide-desktop-small]="hideImageDesktopSmall"
            [colDesktopSmall]="fullWidth ? 12 : isImageMainContent ? 7 : 5"
            [colOrder]="imageColOrder"
            [colTabletOrder]="imageColOrderTablet"
            [colDesktopSmallOrder]="imageColOrderDesktopSmall"
            [pull]="fullWidth ? undefined : 'right'"
            [flMarginBottom]="
              alignment === SectionFeatureAlignment.RIGHT && !imageOverflow
                ? 'large'
                : 'none'
            "
            [flMarginBottomDesktop]="
              fullWidth && alignment === SectionFeatureAlignment.RIGHT
                ? 'large'
                : 'none'
            "
            [flAnimateOnScroll]="animateOnScroll"
            [flAnimateOnScrollBehavior]="
              alignment === SectionFeatureAlignment.RIGHT && animateOnScroll
                ? AnimationBehavior.FADE_FROM_LEFT
                : AnimationBehavior.FADE_FROM_RIGHT
            "
          >
            <figure class="SectionFeature-image-figure">
              <fl-picture
                class="SectionFeature-image-figure-picture"
                [attr.data-image-overflow]="imageOverflow"
                [alignCenter]="true"
                [boundedWidth]="!imageOverflow"
                [fixedAspectRatio]="imageOverflow"
                [display]="PictureDisplay.BLOCK"
                [src]="imageSrc"
                [alt]="imageAlt"
              ></fl-picture>
              <figcaption
                class="SectionFeature-figure-caption"
                *ngIf="imageCaption"
              >
                <fl-text
                  [color]="
                    color === SectionFeatureColor.DARK
                      ? FontColor.FOREGROUND
                      : FontColor.LIGHT
                  "
                  [size]="TextSize.XXSMALL"
                  [fontType]="FontType.SPAN"
                >
                  {{ imageCaption }}
                </fl-text>
              </figcaption>
            </figure>
          </fl-col>
        </fl-grid>
      </fl-container>
    </div>
  `,
  styleUrls: ['./section-feature.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionFeatureComponent implements OnChanges {
  AnimationBehavior = AnimationBehavior;

  @Input() alignment: SectionFeatureAlignment;
  @Input() alignmentTablet?: SectionFeatureAlignment;
  @Input() alignmentDesktopSmall?: SectionFeatureAlignment;
  @Input() borderTop = false;

  @HostBinding('attr.data-bg-color')
  @Input()
  backgroundColor?: SectionFeatureBgColor;

  @HostBinding('attr.data-color')
  @Input()
  color: SectionFeatureColor = SectionFeatureColor.DARK;

  @Input() containerSize: ContainerSize = ContainerSize.DESKTOP_LARGE;
  @Input() imageSrc: string;
  @Input() imageAlt: string;
  @Input() imageCaption?: string;
  /** Stack the content and featured image on top of each other. */
  @Input() fullWidth = false;
  @Input() overlay = false;
  @Input() size: SectionFeatureSize;
  /** If true, image will take up more space on the grid, else the body will */
  @Input() isImageMainContent = true;
  @Input() hideImageMobile = false;
  @Input() hideImageTablet = false;
  @Input() hideImageDesktopSmall = false;
  /** If true, image is full height and full width, without paddings */
  @Input() imageOverflow = false;
  @Input() animateOnScroll = false;

  @ContentChild(SectionFeatureDetailComponent)
  sectionFeatureDetail: SectionFeatureDetailComponent;
  @ContentChild(SectionFeatureHeadingComponent)
  sectionFeatureHeading: SectionFeatureHeadingComponent;
  @ContentChild(SectionFeatureSubheadingComponent)
  sectionFeatureSubheading: SectionFeatureSubheadingComponent;
  @ContentChild(SectionFeatureBodyComponent)
  sectionFeatureBody: SectionFeatureBodyComponent;
  @ContentChild(SectionFeatureButtonComponent)
  sectionFeatureButton: SectionFeatureButtonComponent;
  @ContentChild(SectionFeatureButtonDetailComponent)
  sectionFeatureButtonDetail: SectionFeatureButtonDetailComponent;

  FontColor = FontColor;
  TextSize = TextSize;
  FontType = FontType;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  PictureDisplay = PictureDisplay;
  HorizontalAlignment = HorizontalAlignment;
  VerticalAlignment = VerticalAlignment;
  SectionFeatureAlignment = SectionFeatureAlignment;
  SectionFeatureColor = SectionFeatureColor;
  ContainerSize = ContainerSize;
  PictureObjectFit = PictureObjectFit;

  // NOTE: Type is explicitly set to 1 | 2 instead of `number` to avoid type error
  contentColOrder: 1 | 2;
  contentColOrderTablet?: 1 | 2;
  contentColOrderDesktopSmall?: 1 | 2;
  imageColOrder: 1 | 2;
  imageColOrderTablet?: 1 | 2;
  imageColOrderDesktopSmall?: 1 | 2;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.alignment && 'alignment' in changes) {
      this.imageColOrder = this.getColOrder(this.alignment, false);
      this.contentColOrder = this.getColOrder(this.alignment, true);
    }

    if (this.alignmentTablet && 'alignmentTablet' in changes) {
      this.imageColOrderTablet = this.getColOrder(this.alignmentTablet, false);
      this.contentColOrderTablet = this.getColOrder(this.alignmentTablet, true);
    }

    if (this.alignmentDesktopSmall && 'alignmentDesktopSmall' in changes) {
      this.imageColOrderDesktopSmall = this.getColOrder(
        this.alignmentDesktopSmall,
        false,
      );
      this.contentColOrderDesktopSmall = this.getColOrder(
        this.alignmentDesktopSmall,
        true,
      );
    }
  }

  // If alignment is 'left', the content should be col order 1, and image is col order 2
  // If alignment is 'right', the content should be col order 2, and the image is col order 1
  getColOrder(alignment: SectionFeatureAlignment, isContent: boolean): 1 | 2 {
    if (isContent) {
      return alignment === SectionFeatureAlignment.LEFT ? 1 : 2;
    }
    return alignment === SectionFeatureAlignment.LEFT ? 2 : 1;
  }
}
